import { LIMIT_GAMES } from "./constants";
import type { LimitGame } from "./limitsEntityApi";
import type { LimitsDialogLayoutConfig, LimitsFieldDescriptor } from "../types/limitsDialogLayout.types";

export type LimitsLayoutValidationResult = { ok: true } | { ok: false; errors: string[] }

/** Money scope group id used by the default money block (one currency per group). */
export const DEFAULT_MONEY_SCOPE_GROUP_ID = 'money'

export function validateLimitsLayout(
  config: LimitsDialogLayoutConfig,
): LimitsLayoutValidationResult {
  const errors: string[] = []

  if (!config.groups?.length) {
    errors.push('limitsLayout.groups must be a non-empty array')
    return { ok: false, errors }
  }

  const currencySelectorsByGroup = new Map<string, number>()
  const amountFieldsByGroup = new Map<string, number>()

  for (const g of config.groups) {
    if (!g.id?.trim()) errors.push('Each group must have a non-empty id')
    if (!Array.isArray(g.children)) {
      errors.push(`Group "${g.id}" children must be an array`)
      continue
    }

    for (const field of g.children) {
      const err = validateField(field)
      if (err) errors.push(err)

      if (field.kind === 'currencySelector') {
        const gid = field.scope.groupId
        currencySelectorsByGroup.set(gid, (currencySelectorsByGroup.get(gid) ?? 0) + 1)
      }
      if (field.kind === 'moneyAmount') {
        const gid = field.scope.groupId
        amountFieldsByGroup.set(gid, (amountFieldsByGroup.get(gid) ?? 0) + 1)
      }
    }
  }

  for (const [groupId, count] of currencySelectorsByGroup) {
    if (count !== 1) {
      errors.push(
        `Scope group "${groupId}" must have exactly one currencySelector (found ${count})`,
      )
    }
  }

  for (const [groupId] of amountFieldsByGroup) {
    if (!currencySelectorsByGroup.has(groupId)) {
      errors.push(`Scope group "${groupId}" has moneyAmount field(s) but no currencySelector`)
    }
  }

  if (config.games?.length) {
    const allowed = new Set(Object.values(LIMIT_GAMES))
    for (const game of config.games) {
      if (!allowed.has(game)) {
        errors.push(`Invalid game "${game}" in limitsLayout.games`)
      }
    }
  }

  return errors.length ? { ok: false, errors } : { ok: true }
}

function validateField(field: LimitsFieldDescriptor): string | null {
  if (!field.id?.trim()) return 'Each field must have a non-empty id'

  switch (field.kind) {
    case 'scalarNumber': {
      if ('scope' in field && (field as { scope?: unknown }).scope !== undefined) {
        return `Field "${field.id}": scalarNumber must not have scope`
      }
      return null
    }
    case 'currencySelector': {
      if (field.scope.role !== 'currency') {
        return `Field "${field.id}": currencySelector must use scope.role "currency"`
      }
      return null
    }
    case 'moneyAmount': {
      if (field.scope.role !== 'amount') {
        return `Field "${field.id}": moneyAmount must use scope.role "amount"`
      }
      return null
    }
  }
}

/** Optional field toggles when building the standard limits layout for a dialog. */
export type LimitsLayoutToggles = {
  showMinSelections?: boolean
  showMaxSelections?: boolean
  showMinStake?: boolean
  showMaxStake?: boolean
  showMaxWinnings?: boolean
  games?: LimitGame[]
}

/**
 * Standard two-group limits layout (main selections + per-currency money fields).
 * Pass toggles to omit specific fields. Uses one money scope group for shared currency.
 */
export function buildLimitsLayout(toggles: LimitsLayoutToggles = {}): LimitsDialogLayoutConfig {
  const {
    showMinSelections = true,
    showMaxSelections = true,
    showMinStake = true,
    showMaxStake = true,
    showMaxWinnings = true,
    games = [LIMIT_GAMES.GLOBAL],
  } = toggles

  const groups: LimitsDialogLayoutConfig['groups'] = []

  if (showMinSelections || showMaxSelections) {
    const children: LimitsFieldDescriptor[] = []
    if (showMinSelections) {
      children.push({
        id: 'min-selections',
        kind: 'scalarNumber',
        label: 'common.labels.min_selections',
        bind: 'minSelections',
        placeholder: 'common.placeholders.enter_value',
      })
    }
    if (showMaxSelections) {
      children.push({
        id: 'max-selections',
        kind: 'scalarNumber',
        label: 'common.labels.max_selections',
        bind: 'maxSelections',
        placeholder: 'common.placeholders.enter_value',
      })
    }
    groups.push({
      id: 'main-limits',
      label: 'common.sections.main_limits',
      children,
    })
  }

  if (showMinStake || showMaxStake || showMaxWinnings) {
    const children: LimitsFieldDescriptor[] = [
      {
        id: 'currency',
        kind: 'currencySelector',
        label: 'common.labels.currency',
        scope: { groupId: DEFAULT_MONEY_SCOPE_GROUP_ID, role: 'currency' },
      },
    ]
    if (showMinStake) {
      children.push({
        id: 'min-stake',
        kind: 'moneyAmount',
        label: 'common.labels.min_stake',
        bind: 'minStake',
        placeholder: 'common.placeholders.enter_value',
        scope: { groupId: DEFAULT_MONEY_SCOPE_GROUP_ID, role: 'amount' },
      })
    }
    if (showMaxStake) {
      children.push({
        id: 'max-stake',
        kind: 'moneyAmount',
        label: 'common.labels.max_stake',
        bind: 'maxStake',
        placeholder: 'common.placeholders.enter_value',
        scope: { groupId: DEFAULT_MONEY_SCOPE_GROUP_ID, role: 'amount' },
      })
    }
    if (showMaxWinnings) {
      children.push({
        id: 'max-winnings',
        kind: 'moneyAmount',
        label: 'common.labels.max_winnings',
        bind: 'maxWinnings',
        placeholder: 'common.placeholders.enter_value',
        scope: { groupId: DEFAULT_MONEY_SCOPE_GROUP_ID, role: 'amount' },
      })
    }
    groups.push({
      id: 'money-limits',
      label: 'common.sections.money_limits',
      children,
    })
  }

  return { groups, games }
}
